// import { Badge } from 'antd';
import MyAvatar from '@/components/my-avatar';
import { TagTypes } from '@/components/my-avatar/type';
import { formatMessageTime } from '@/scripts/utils/date';
// import { useChatMessages } from '@/contexts/chat/scripts/hooks/useChatMessages';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import GroupMembersIcon from '@/assets/images/common/svg/group-members-icon.svg?react';
import { useChatContext, useLastMessage } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { ChatMessagePulled } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { DebugTooltip } from '@/scripts/utils/devUtils';
import MessageStatus from '@/pages/messages/components/message-status';
import { serverTime } from '@/contexts/chat/scripts/hooks/useSystemTime/utils';
import { isSystemMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import { GroupEncryptionStatus } from '@/configs/apis/groups/interface';
import LastMessageRenderer from './components/lastMessageRenderer';
import styles from './index.module.scss';
// import AiIcon from './images/ai.svg?react';
// import VipIcon from './images/vip.svg?react';
// import MuteIcon from './images/mute.svg?react';

const MessageItem = ({
  onClick,
  chat,
  activatedChatId,
}: {
  onClick: (chat: Chat) => void;
  chat: Chat;
  activatedChatId: string | undefined;
}) => {
  const { getUnreadMessageCount, chatMessages, chatGroup, chatsState, ready } =
    useChatContext();
  const [lastUser, setLastUser] = useState<ChatGroupMember>();

  const { lastMessage, trigger, run: runGetLastMessage } = useLastMessage(chat);

  const [unreadCount, setUnreadCount] = useState(0);

  const isEffectiveMsg = useMemo(
    () => lastMessage && lastMessage.pulled === ChatMessagePulled.YES,
    [lastMessage]
  );

  const refs = useRefs({
    chat,
    chatGroup,
    chatMessages,
    chatsState,
    getUnreadMessageCount,
  });

  const groupInfo = useMemo(() => {
    return chatGroup.chatGroupState.getGroup(chat.chat_id);
  }, [chat.chat_id, chatGroup.chatGroupState]);

  useEffect(() => {
    if (ready) {
      const timer = setTimeout(async () => {
        const { chatsState, getUnreadMessageCount } = refs.current;

        const count = await getUnreadMessageCount(
          chat.chat_id,
          chat.last_read_message_id
        );

        chatsState.updateChatReadCount(chat.chat_id, count);
        setUnreadCount(count);
      }, 500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [refs, chat.chat_id, ready, trigger, chat.last_read_message_id]);

  useEffect(() => {
    if (ready) {
      (async () => {
        const { chat, chatGroup } = refs.current;
        if (
          isEffectiveMsg &&
          chat.category === ChatCategory.GROUP &&
          lastMessage!.owner_id &&
          !isSystemMessage(lastMessage)
        ) {
          const [
            {
              args: [user],
            },
          ] = await chatGroup.getMembersByUserId([
            { id: chat.chat_id, args: [lastMessage!.owner_id] },
          ]);
          setLastUser(user);
        } else {
          setLastUser(undefined);
        }
      })();
    }
  }, [isEffectiveMsg, lastMessage, refs, ready]);

  return (
    <DebugTooltip
      info={{
        chat,
        lastMessage,
        lastUser,
      }}
      excludeFields={['user']}
    >
      <div
        onClick={() => onClick(chat)}
        className={cls(
          styles.messageItem,
          chat.chat_id === activatedChatId && styles.active
        )}
      >
        <MyAvatar
          src={chat.avatar}
          className={styles.avatar}
          tagType={TagTypes.DESTROY}
          expireDuration={chat.expire_duration}
        ></MyAvatar>
        <div className={styles.itemContainer}>
          <div className={styles.itemTop}>
            <div className={styles.topLeft}>
              {chat.category === ChatCategory.GROUP && (
                <div className={styles.chatTypeTag}>
                  {/* <AiIcon /> */}
                  {chat.category === ChatCategory.GROUP && <GroupMembersIcon />}
                </div>
              )}
              <div>
                {chat.category === ChatCategory.GROUP &&
                  groupInfo?.is_encryption === GroupEncryptionStatus.NO &&
                  '[Unencrypted]'}
              </div>
              <div className={cls(styles.toUserName)}>{chat.name}</div>
              {/* <div className={styles.vipTag}>
              <VipIcon></VipIcon>
            </div>
            <div className={styles.ensLabel}>kelas.eth</div> */}
              {/* <div className={styles.enterpriseLabel}>@Halo.Chat</div> */}
              {/* <div className={styles.groupTag}>External</div>
            <div className={styles.userStatus}>do not disturb</div> */}
            </div>
            <div className={styles.topRight}>
              {/* <MuteIcon></MuteIcon> */}
              <div className={styles.time}>
                {formatMessageTime(chat.update_at, serverTime.time)}
              </div>
            </div>
          </div>
          <div className={styles.itemBottom}>
            <div className={styles.bottomLeft}>
              {/* <div className={styles.msgLabel}>[OTP]</div> */}
              {isEffectiveMsg && (
                <MessageStatus chat={chat} message={lastMessage!} showReader={false} />
              )}
              {lastUser && <div className={styles.userName}>{lastUser.name}:</div>}
              {ready && (
                <LastMessageRenderer
                  chat={chat}
                  message={lastMessage}
                  setLastMessage={runGetLastMessage}
                ></LastMessageRenderer>
              )}
            </div>
            {unreadCount > 0 && (
              <Badge size="small" count={unreadCount} overflowCount={99}></Badge>
            )}
          </div>
        </div>
      </div>
    </DebugTooltip>
  );
};

export default memo(MessageItem);
